import type { AppRouter } from '@/App'
import { WorkosAuthLoader } from '@/components/auth/WorkosAuthLoader'
import LoadingScreen from '@/components/loading/loading'
import { ROUTES } from '@/constants/routeConstants'
import { AuthKitProvider, useAuth } from '@workos-inc/authkit-react'
import type { ReactNode } from 'react'
import { useEffect } from 'react'

const RequireSignIn = ({ children }: { children: ReactNode }) => {
  const { isLoading, user, signIn } = useAuth()

  useEffect(() => {
    if (!isLoading && !user) {
      void signIn()
    }
  }, [user, signIn, isLoading])

  if (isLoading || !user) {
    return <LoadingScreen delay={300} />
  }

  return children
}

export function WorkosProvider({
  router,
  children,
}: {
  router: AppRouter
  children: ReactNode
}) {
  const isWorkosEnabled = window.env.WORKOS_ENABLED

  if (!isWorkosEnabled) {
    return <>{children}</>
  }

  return (
    <AuthKitProvider
      devMode={['test', 'development'].includes(window.env.APP_ENV)}
      clientId={window.env.WORKOS_CLIENT_ID}
      apiHostname={window.env.WORKOS_API_HOSTNAME}
      redirectUri={window.location.origin}
      onRedirectCallback={({ state }) => {
        console.log('onRedirectCallback', state)
        const returnPath =
          state?.returnTo && typeof state.returnTo === 'string'
            ? state.returnTo
            : ROUTES.HOME

        // Use router instance directly for navigation
        void router.navigate({ to: returnPath, replace: true })
      }}
    >
      <WorkosAuthLoader />
      <RequireSignIn>{children}</RequireSignIn>
    </AuthKitProvider>
  )
}
